import React, { useEffect, useState } from "react";
import { Tag, Drawer, Typography, Divider, Row, Col, Table } from "antd";

import { DateFormat, getApi } from "../../../helpers";
import CardContainer from "../../../components/Card";
import TableContainer from "../../../components/Table";

const { Title, Text } = Typography;

const loyaltyColumns = [
  {
    title: "Customer Name",
    dataIndex: "customer_name",
    key: "customer_name",
    sorter: true,
  },
  {
    title: "Mobile",
    dataIndex: "customer_mobile",
    key: "customer_mobile",
  },
  {
    title: "Start Date",
    dataIndex: "start_date",
    key: "start_date",
    align: "center",
    render: (text) => DateFormat(text, "DD MMM YYYY"),
    sorter: true,
  },
  {
    title: "Expiry Date",
    dataIndex: "expiry_date",
    key: "expiry_date",
    align: "center",
    render: (text) => DateFormat(text, "DD MMM YYYY"),
    sorter: true,
  },
  {
    title: "Discount %",
    dataIndex: "discount_percentage",
    key: "discount_percentage",
    align: "center",
    render: (text) => `${text}%`,
    sorter: true,
  },
  {
    title: "Lifetime Savings",
    dataIndex: "total_lifetime_savings",
    key: "total_lifetime_savings",
    align: "center",
    render: (text) => `₹${text}`,
    sorter: true,
  },
  {
    title: "Purchased From",
    dataIndex: "purchased_source",
    key: "purchased_source",
    align: "center",
    render: (text) => {
      const sourceText = text || "Unknown";
      const color = sourceText.toLowerCase() === "store" ? "blue" : "green";
      return (
        <Tag color={color}>
          {sourceText.charAt(0).toUpperCase() + sourceText.slice(1)}
        </Tag>
      );
    },
    sorter: true,
  },
  {
    title: "Expiring In",
    dataIndex: "expiring_in",
    key: "expiring_in",
    align: "center",
    render: (text) => {
      // Check if expired
      if (text === "expired" || text === "Expired") {
        return (
          <Tag color="red">
            Expired
          </Tag>
        );
      }

      const days = parseInt(text);

      // Check if NaN or invalid number
      if (isNaN(days)) {
        return (
          <Tag color="red">
            Expired
          </Tag>
        );
      }

      let color = "green";
      if (days <= 7) color = "red";
      else if (days <= 30) color = "orange";

      return (
        <Tag color={color}>
          {days} days
        </Tag>
      );
    },
    sorter: true,
  },
  {
    title: "Status",
    dataIndex: "is_active",
    key: "is_active",
    align: "center",
    render: (text) => (
      <Tag color={text ? "green" : "red"}>
        {text ? "Active" : "Inactive"}
      </Tag>
    ),
    sorter: true,
  },
  {
    title: "Created Date",
    dataIndex: "created_at",
    key: "created_at",
    align: "center",
    render: (text) => DateFormat(text),
    sorter: true,
  },
];

const orderSavingsColumns = [
  {
    title: "Order ID",
    dataIndex: "order_id",
    key: "order_id",
    width: 100,
  },
  {
    title: "Order Amount",
    dataIndex: "order_amount",
    key: "order_amount",
    align: "center",
    render: (text) => `₹${text}`,
    width: 120,
  },
  {
    title: "Savings Amount",
    dataIndex: "savings_amount",
    key: "savings_amount",
    align: "center",
    render: (text) => `₹${text}`,
    width: 130,
  },
  {
    title: "Order Date",
    dataIndex: "order_date",
    key: "order_date",
    align: "center",
    render: (text) => DateFormat(text, "DD MMM YYYY"),
    width: 120,
  },
  {
    title: "Savings Date",
    dataIndex: "savings_date",
    key: "savings_date",
    align: "center",
    render: (text) => DateFormat(text),
    width: 150,
  },
];

export default function Loyalty() {
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(8);
  const [loyaltyList, setLoyaltyList] = useState([]);
  const [savedPayload, setSavedPayload] = useState({});
  
  // Sidebar states
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [loyaltyDetails, setLoyaltyDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  useEffect(() => {
    getLoyaltyList();
  }, []);

  const getLoyaltyList = async (payload = {}) => {
    setLoading(true);
    
    // Prepare the payload according to the API format
    const requestPayload = {
      key: "JWT",
      secret: "RAPSAP",
      page: payload.page || 0,
      size: payload.size || 10,
      ...payload,
    };

    // Add sorting if provided
    if (!requestPayload.sort) {
      requestPayload.sort = {
        field: "created_at",
        order_by: "desc"
      };
    }

    try {
      const res = await getApi(requestPayload, "getLoyaltyList", "dashboard/");
      if (res.data) {
        setLoyaltyList(res.data);
        setTotal(res.meta?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching loyalty list:", error);
      setLoyaltyList([]);
      setTotal(0);
    }
    
    setSavedPayload(payload);
    setLoading(false);
  };

  const getLoyaltyDetails = async (userId) => {
    setDetailsLoading(true);
    
    const requestPayload = {
      key: "JWT",
      secret: "RAPSAP",
      user_id: userId,
      page: 0,
      size: 1000, // Get all data since pagination is handled in frontend
    };

    try {
      const res = await getApi(requestPayload, "getLoyaltyDetails", "dashboard/");
      if (res.data) {
        setLoyaltyDetails(res.data);
        setCurrentPage(1); // Reset to first page when new data loads
      }
    } catch (error) {
      console.error("Error fetching loyalty details:", error);
      setLoyaltyDetails(null);
    }
    
    setDetailsLoading(false);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let payload = { ...savedPayload };
    
    if (sorter && sorter.field) {
      payload.sort = {
        field: sorter.field,
        order_by: sorter.order === 'ascend' ? 'asc' : 'desc'
      };
    }
    
    getLoyaltyList(payload);
  };

  const handleRowClick = (record) => {
    setSidebarVisible(true);
    getLoyaltyDetails(record.user_id);
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
  };

  const closeSidebar = () => {
    setSidebarVisible(false);
    setLoyaltyDetails(null);
    setCurrentPage(1);
  };

  // Calculate paginated data for order savings
  const getPaginatedOrderSavings = () => {
    if (!loyaltyDetails?.order_savings) return [];
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return loyaltyDetails.order_savings.slice(startIndex, endIndex);
  };

  return (
    <>
      <CardContainer bordered={false} hoverable={false}>
        <TableContainer
          breadcrumbTitle="Loyalty Management"
          loading={loading}
          total={total}
          columns={loyaltyColumns}
          data={loyaltyList}
          searchFor="Customer Name, Mobile"
          functionsHandler={getLoyaltyList}
          rowClickHandler={handleRowClick}
          onChange={handleTableChange}
          scrollable={true}
        />
      </CardContainer>

      <Drawer
        title="Loyalty Details"
        placement="right"
        width={800}
        onClose={closeSidebar}
        open={sidebarVisible}
        destroyOnClose={true}
        maskClosable={true}
      >
        {detailsLoading ? (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <Text>Loading loyalty details...</Text>
          </div>
        ) : loyaltyDetails ? (
          <div style={{ padding: '0 4px' }}>
            {/* Customer Information */}
            <div style={{ 
              marginBottom: '24px', 
              backgroundColor: '#F7F7F7', 
              borderRadius: '12px', 
              padding: '16px' 
            }}>
              {/* Header */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ 
                  display: "flex", 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start', 
                  marginBottom: '8px' 
                }}>
                  <Title style={{ 
                    margin: 0, 
                    fontSize: '24px',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    flex: 1
                  }} level={3}>
                    {loyaltyDetails.customer_name}
                  </Title>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      padding: "4px 12px",
                      textTransform: "uppercase",
                      borderRadius: '6px',
                      marginLeft: '12px',
                      backgroundColor: loyaltyDetails.is_active ? '#f6ffed' : '#fff2f0',
                      color: loyaltyDetails.is_active ? '#52c41a' : '#ff4d4f',
                      border: loyaltyDetails.is_active ? '1px solid #b7eb8f' : '1px solid #ffccc7'
                    }}
                  >
                    {loyaltyDetails.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  Loyalty ID: {loyaltyDetails.loyalty_id} • Mobile: {loyaltyDetails.customer_mobile}
                </Text>
              </div>

              {/* Customer Details Grid */}
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <div className="lblc">
                    <Text style={{ fontSize: 11 }} type="secondary">
                      Discount Percentage
                    </Text>
                    <br />
                    <Text style={{ fontSize: '16px', fontWeight: 600, color: '#1890ff' }}>
                      {loyaltyDetails.discount_percentage}%
                    </Text>
                  </div>
                </Col>
                
                <Col xs={24} md={12}>
                  <div className="lblc">
                    <Text style={{ fontSize: 11 }} type="secondary">
                      Total Lifetime Savings
                    </Text>
                    <br />
                    <Text style={{ fontSize: '16px', fontWeight: 600, color: '#52c41a' }}>
                      ₹{loyaltyDetails.total_lifetime_savings}
                    </Text>
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <div className="lblc">
                    <Text style={{ fontSize: 11 }} type="secondary">
                      Start Date
                    </Text>
                    <br />
                    <Text>{DateFormat(loyaltyDetails.start_date, "DD MMM YYYY")}</Text>
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <div className="lblc">
                    <Text style={{ fontSize: 11 }} type="secondary">
                      Expiry Date
                    </Text>
                    <br />
                    <Text>{DateFormat(loyaltyDetails.expiry_date, "DD MMM YYYY")}</Text>
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <div className="lblc">
                    <Text style={{ fontSize: 11 }} type="secondary">
                      Expiring In
                    </Text>
                    <br />
                    {loyaltyDetails.expiring_in === "expired" || loyaltyDetails.expiring_in === "Expired" || isNaN(parseInt(loyaltyDetails.expiring_in)) ? (
                      <Tag color="red">
                        Expired
                      </Tag>
                    ) : (
                      <Tag color={
                        loyaltyDetails.expiring_in <= 7 ? "red" :
                        loyaltyDetails.expiring_in <= 30 ? "orange" : "green"
                      }>
                        {loyaltyDetails.expiring_in} days
                      </Tag>
                    )}
                  </div>
                </Col>

                <Col xs={24} md={12}>
                  <div className="lblc">
                    <Text style={{ fontSize: 11 }} type="secondary">
                      Created Date
                    </Text>
                    <br />
                    <Text>{DateFormat(loyaltyDetails.created_at)}</Text>
                  </div>
                </Col>
              </Row>
            </div>

            <Divider style={{ margin: '24px 0' }} />

            {/* Order Savings Table */}
            <div>
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ fontSize: '18px', fontWeight: 600 }}>
                  Order Savings History
                </Text>
                {loyaltyDetails.order_savings && (
                  <Text style={{ 
                    fontSize: '14px', 
                    color: '#666', 
                    marginLeft: '12px' 
                  }}>
                    ({loyaltyDetails.order_savings.length} transactions)
                  </Text>
                )}
              </div>
              
              {loyaltyDetails.order_savings && loyaltyDetails.order_savings.length > 0 ? (
                <div style={{ 
                  backgroundColor: '#fff', 
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                  overflow: 'hidden'
                }}>
                  <Table
                    loading={false}
                    columns={orderSavingsColumns}
                    dataSource={getPaginatedOrderSavings()}
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      total: loyaltyDetails.order_savings.length,
                      onChange: handlePageChange,
                      showSizeChanger: false,
                      showQuickJumper: false,
                      showTotal: (total, range) => 
                        `${range[0]}-${range[1]} of ${total} transactions`,
                      size: 'small'
                    }}
                    scroll={{ x: true }}
                    bordered={false}
                    size="small"
                    rowKey={(record, index) => `savings-${index}`}
                  />
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 0',
                  backgroundColor: '#fafafa',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0'
                }}>
                  <Text type="secondary" style={{ fontSize: '16px' }}>
                    No order savings found
                  </Text>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '50px 0',
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            margin: '20px'
          }}>
            <Text type="secondary" style={{ fontSize: '16px' }}>
              No data available
            </Text>
          </div>
        )}
      </Drawer>
    </>
  );
}